// Purpose: sessionStorage hand-off for flows that bounce through a
// full-page navigation (OIDC step-up) and want to resume on the same
// page without the operator re-clicking.
//
// Commit 8b.6: the owner Recover Link flow posts to the API, gets
// 428 if MFA is stale, the axios interceptor navigates the tab to
// /auth/step-up, Entra round-trips, the step-up callback stamps
// mfaSatisfiedAt, the server 302s back to the SPA detail page. At
// that point the operator would have had to click Recover Link again
// to see the URL. Stashing the intent in sessionStorage before the
// 428 is raised and consuming it on the detail-page mount eliminates
// that second click.
//
// sessionStorage, not localStorage: scoped to the tab + origin, clears
// when the tab closes, can't leak across browser profiles. The MFA
// round-trip is same-tab same-origin navigation, so the stash
// survives by design.

const STORAGE_KEY = 'ast.pendingAction';
const TTL_MS = 5 * 60 * 1000;

export type PendingRecoverLink = {
  type: 'recover-link';
  resource: 'share' | 'dropbox';
  id: string;
  // Commit 8b.24 (P2): which recovery to resume after the step-up bounce.
  // 'link' re-fetches the share/public link; 'recipients' re-fetches the
  // per-recipient access codes. Both flows share this one stash slot, so
  // without this discriminator a 'recipients' intent resumed as a 'link'
  // recovery (wrong result, forcing a second click). A pre-8b.24 stash
  // with no action field defaults to 'link' on read (back-compat).
  action: 'link' | 'recipients';
  // Absolute epoch ms. A stash from a forgotten tab a day later
  // should not suddenly auto-fire; consume() drops it as expired.
  expiresAt: number;
};

// Commit 8b.12.1: same bounce-and-resume pattern for the break-glass
// grant flow. The Forensics page POST hits 428 if MFA is stale; the
// axios interceptor redirects to step-up; after MFA the SPA returns to
// /admin/forensics. Without a stash the operator would have to re-fill
// the form and re-click Grant. With a stash, the Forensics page mount
// consumes it and auto-fires once.
//
// Server-side mfa middleware enforces freshness regardless of what the
// stash claims, so a stale stash that somehow survives the 5-min TTL
// can't actually break-glass anything — it just gets a fresh 428 and
// the cycle stops (consume already cleared the stash). Defense in depth.
export type PendingForensicsGrant = {
  type: 'forensics-grant';
  targetType: 'share' | 'dropbox_upload';
  targetId: string;
  justification: string;
  acknowledged: boolean;
  expiresAt: number;
};

type AnyPending = PendingRecoverLink | PendingForensicsGrant;

function storage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage;
  } catch {
    // Private mode in some browsers throws on sessionStorage access.
    return null;
  }
}

export function stashPendingRecoverLink(
  resource: PendingRecoverLink['resource'],
  id: string,
  action: PendingRecoverLink['action'] = 'link'
): void {
  const s = storage();
  if (!s) return;
  const entry: PendingRecoverLink = {
    type: 'recover-link',
    resource,
    id,
    action,
    expiresAt: Date.now() + TTL_MS,
  };
  try {
    s.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Quota / disabled — non-fatal. Operator just has to click again.
  }
}

export function clearPendingRecoverLink(): void {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(STORAGE_KEY);
  } catch {
    // Non-fatal.
  }
}

export function stashPendingForensicsGrant(
  input: Omit<PendingForensicsGrant, 'type' | 'expiresAt'>
): void {
  const s = storage();
  if (!s) return;
  const entry: PendingForensicsGrant = {
    type: 'forensics-grant',
    targetType: input.targetType,
    targetId: input.targetId,
    justification: input.justification,
    acknowledged: input.acknowledged,
    expiresAt: Date.now() + TTL_MS,
  };
  try {
    s.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Quota / disabled — non-fatal. Operator just has to click again.
  }
}

export function clearPendingForensicsGrant(): void {
  // Same key as recover-link — only one pending action at a time. The
  // separate clear function exists for symmetry + readability at the
  // call site.
  clearPendingRecoverLink();
}

// Returns the stash if one exists, matches resource+id, and hasn't
// expired — and removes it in the process. On mismatch or expiry the
// stash is cleared (don't leave stale entries lying around to trip a
// later mount).
export function consumePendingRecoverLink(
  resource: PendingRecoverLink['resource'],
  id: string
): PendingRecoverLink | null {
  const parsed = readAndClear();
  if (!parsed) return null;
  if (parsed.type !== 'recover-link') return null;
  if (parsed.resource !== resource) return null;
  if (parsed.id !== id) return null;
  if (parsed.expiresAt <= Date.now()) return null;
  return parsed;
}

// Forensics-grant variant. No resource+id key — the page knows it's
// the forensics page; any pending forensics-grant on mount is for it.
export function consumePendingForensicsGrant(): PendingForensicsGrant | null {
  const parsed = readAndClear();
  if (!parsed) return null;
  if (parsed.type !== 'forensics-grant') return null;
  if (parsed.expiresAt <= Date.now()) return null;
  return parsed;
}

// Shared: read whatever pending action is in the stash (could be either
// variant), parse + validate basic shape, and clear unconditionally. A
// stash for the wrong page is dropped on the floor — better than
// surviving across pages where it could trip an unrelated consume().
function readAndClear(): AnyPending | null {
  const s = storage();
  if (!s) return null;
  let raw: string | null;
  try {
    raw = s.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    s.removeItem(STORAGE_KEY);
  } catch {
    // Non-fatal.
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const p = parsed as Partial<AnyPending>;
  if (typeof p.expiresAt !== 'number') return null;
  if (p.type === 'recover-link') {
    const r = p as Partial<PendingRecoverLink>;
    if (typeof r.resource !== 'string' || typeof r.id !== 'string') return null;
    return {
      type: 'recover-link',
      resource: r.resource as PendingRecoverLink['resource'],
      id: r.id,
      // Back-compat: a pre-8b.24 stash has no action → treat as 'link'.
      action: r.action === 'recipients' ? 'recipients' : 'link',
      expiresAt: p.expiresAt,
    };
  }
  if (p.type === 'forensics-grant') {
    const r = p as Partial<PendingForensicsGrant>;
    if (
      typeof r.targetType !== 'string' ||
      typeof r.targetId !== 'string' ||
      typeof r.justification !== 'string' ||
      typeof r.acknowledged !== 'boolean'
    ) {
      return null;
    }
    return {
      type: 'forensics-grant',
      targetType: r.targetType as PendingForensicsGrant['targetType'],
      targetId: r.targetId,
      justification: r.justification,
      acknowledged: r.acknowledged,
      expiresAt: p.expiresAt,
    };
  }
  return null;
}
